import React, { useState, useEffect } from "react";
import {
  ActivityIndicator,
  View,
  StyleSheet,
  ScrollView,
  Switch,
  Modal,
  Pressable,
  Text,
  useWindowDimensions,
} from "react-native";
import AISetting from "./AISetting";
import {
  PrimaryButton,
  SecondaryButton,
  RainbowBorder,
  StickyOnboardingModal,
  beforeAfterSteps,
} from "../../../../common/native";
import { Picture, Restart } from "../../../../../assets";
import { MultiImageUpload } from "../../../../common/web";
import { NormalText } from "../../../../common/native";
import { useOnboardingTips } from "../../../../../contexts/OnboardingTipsContext";
import { TTooltipStep, TOOLTIP_STEPS } from "../../../../../utils/constant";
import { APP_TOKENS as tokens } from "../../../../../styles/tokens";

const STEP_ONE_TOKENS = {
  size: {
    tabletBreakpoint: 1024,
    contentMaxWidth: 1120,
    warningModalMaxWidth: 620,
    warningListMaxHeight: 360,
    warningPreview: 64,
  },
} as const;

const getCurrentBeforeAfterStep = (
  tipsState: Partial<Record<TTooltipStep, boolean>>,
): TTooltipStep | null => {
  if (!tipsState.usePlaceholderImages)
    return TOOLTIP_STEPS.USE_PLACEHOLDER_IMAGES;
  if (!tipsState.useAISettings) return TOOLTIP_STEPS.USE_AI_SETTINGS;
  if (!tipsState.generateImages) return TOOLTIP_STEPS.GENERATE_IMAGES;
  return null;
};

type Step1Props = {
  loading: boolean;
  setStep: (step: number) => void;
  aiSetting: string[];
  setAiSetting: (values: string[]) => void;
  uploadedImages: { preOp: Blob[]; postOp: Blob[] };
  handleGenerateBeforeAfter: () => void;
  canGenerateWithoutUploads?: boolean;
  handleUpdateUploadedImages: (
    images: Blob[],
    type: "preOp" | "postOp",
  ) => void;
  tipsEnabled?: boolean;
};

type TImageResolutionItem = {
  key: string;
  group: "Before" | "After";
  sourceType: "preOp" | "postOp";
  sourceIndex: number;
  width: number;
  height: number;
  minDimension: number;
  previewUrl: string;
};

type TPlaceholderRequestStatus = "idle" | "loading" | "ready" | "error";

const Step1: React.FC<Step1Props> = ({
  loading,
  setStep,
  aiSetting,
  setAiSetting,
  uploadedImages,
  handleGenerateBeforeAfter,
  canGenerateWithoutUploads = false,
  handleUpdateUploadedImages,
  tipsEnabled = false,
}) => {
  const { width } = useWindowDimensions();
  const usesStackedUploads = width < STEP_ONE_TOKENS.size.tabletBreakpoint;
  const [usePlaceholderImages, setUsePlaceholderImages] = useState(false);
  const [placeholderRequestStatus, setPlaceholderRequestStatus] =
    useState<TPlaceholderRequestStatus>("idle");
  const [placeholderRequestError, setPlaceholderRequestError] = useState("");
  const [placeholderRetryKey, setPlaceholderRetryKey] = useState(0);
  const [isPreparingPreOpImages, setIsPreparingPreOpImages] = useState(false);
  const [isPreparingPostOpImages, setIsPreparingPostOpImages] = useState(false);
  const [isCheckingImageDimensions, setIsCheckingImageDimensions] =
    useState(false);
  const [showSmallImageModal, setShowSmallImageModal] = useState(false);
  const [smallImageRows, setSmallImageRows] = useState<TImageResolutionItem[]>(
    [],
  );
  const previewUrlsRef = React.useRef<string[]>([]);
  const prevUsePlaceholderImages = React.useRef(usePlaceholderImages);
  const smallImageDialogRef = React.useRef<HTMLDivElement | null>(null);
  const smallImageTriggerRef = React.useRef<HTMLElement | null>(null);
  const isCheckingImageDimensionsRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  const [focusedDeleteKey, setFocusedDeleteKey] = useState<string | null>(null);
  const { tipsState, markStepComplete } = useOnboardingTips();
  const isPreparingUploads = isPreparingPreOpImages || isPreparingPostOpImages;

  // Placeholder image URLs - replace these with your actual URLs
  const PLACEHOLDER_IMAGES = {
    preOp: [
      "https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/user-app-assets/before-after-samples/before_front.png",
      "https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/user-app-assets/before-after-samples/before_oblique.png",
    ],
    postOp: [
      "https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/user-app-assets/before-after-samples/after_front.png",
      "https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/user-app-assets/before-after-samples/after_oblique.png",
    ],
  };

  // Effect to detect when AI settings are changed
  const onAiSettingChange = (newSettings: string[]) => {
    setAiSetting(newSettings);

    if (newSettings.length > 0 && tipsState.useAISettings) {
      markStepComplete("useAISettings");
      // console.log("AI settings marked as complete");
    }
  };

  useEffect(() => {
    if (!usePlaceholderImages) {
      setPlaceholderRequestStatus("idle");
      setPlaceholderRequestError("");
      if (prevUsePlaceholderImages.current) {
        handleUpdateUploadedImages([], "preOp");
        handleUpdateUploadedImages([], "postOp");
      }
      prevUsePlaceholderImages.current = false;
      return;
    }

    const controller = new AbortController();
    let isCurrentRequest = true;

    const fetchPlaceholderImages = async () => {
      setPlaceholderRequestStatus("loading");
      setPlaceholderRequestError("");

      try {
        const preOpBlobs = await Promise.all(
          PLACEHOLDER_IMAGES.preOp.map(async (url) => {
            const response = await fetch(url, {
              signal: controller.signal,
              cache: "no-store",
            });
            if (!response.ok) {
              throw new Error(
                `Placeholder request failed (${response.status})`,
              );
            }
            return response.blob();
          }),
        );

        const postOpBlobs = await Promise.all(
          PLACEHOLDER_IMAGES.postOp.map(async (url) => {
            const response = await fetch(url, {
              signal: controller.signal,
              cache: "no-store",
            });
            if (!response.ok) {
              throw new Error(
                `Placeholder request failed (${response.status})`,
              );
            }
            return response.blob();
          }),
        );

        if (!isCurrentRequest || controller.signal.aborted) return;
        handleUpdateUploadedImages(preOpBlobs, "preOp");
        handleUpdateUploadedImages(postOpBlobs, "postOp");
        setPlaceholderRequestStatus("ready");
      } catch (error) {
        if (!isCurrentRequest || controller.signal.aborted) return;
        console.error("Error fetching placeholder images:", error);
        setPlaceholderRequestStatus("error");
        setPlaceholderRequestError(
          "Placeholder images could not be loaded. Check your connection and try again.",
        );
      }
    };

    void fetchPlaceholderImages();

    if (!tipsState.usePlaceholderImages) {
      markStepComplete("usePlaceholderImages");
    }

    prevUsePlaceholderImages.current = true;

    return () => {
      isCurrentRequest = false;
      controller.abort();
    };
  }, [usePlaceholderImages, placeholderRetryKey]);

  const getBlobDimensions = (
    blob: Blob,
  ): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();

      img.onload = () => {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        URL.revokeObjectURL(objectUrl);
        resolve({ width, height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Unable to read image dimensions."));
      };

      img.src = objectUrl;
    });

  const clearSmallImageRows = React.useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setSmallImageRows([]);
  }, []);

  const replaceSmallImageRows = React.useCallback(
    (rows: TImageResolutionItem[]) => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current = rows.map((row) => row.previewUrl);
      setSmallImageRows(rows);
    },
    [],
  );

  const restoreSmallImageTriggerFocus = React.useCallback(() => {
    const savedTrigger = smallImageTriggerRef.current;
    const trigger =
      savedTrigger?.isConnected === true
        ? savedTrigger
        : document.querySelector<HTMLElement>('[aria-label="Generate"]');
    if (trigger?.isConnected) trigger.focus();
  }, []);

  const closeSmallImageModal = React.useCallback(() => {
    setShowSmallImageModal(false);
    clearSmallImageRows();
    window.setTimeout(restoreSmallImageTriggerFocus, tokens.motion.slow * 2);
  }, [clearSmallImageRows, restoreSmallImageTriggerFocus]);

  React.useEffect(() => {
    if (!showSmallImageModal) return;

    const initialFocus =
      smallImageDialogRef.current?.querySelector<HTMLElement>(
        '[aria-label="Cancel small image warning"]',
      );
    window.requestAnimationFrame(() => initialFocus?.focus());
  }, [showSmallImageModal]);

  React.useEffect(() => {
    if (showSmallImageModal || !smallImageTriggerRef.current) return;

    const timeout = window.setTimeout(() => {
      window.requestAnimationFrame(restoreSmallImageTriggerFocus);
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [restoreSmallImageTriggerFocus, showSmallImageModal]);

  const handleSmallImageDialogKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeSmallImageModal();
      return;
    }

    if (event.key !== "Tab") return;
    const focusableElements = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [role="button"]:not([aria-disabled="true"]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (!focusableElements.length) return;

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearSmallImageRows();
    };
  }, [clearSmallImageRows]);

  const buildResolutionRows = async (
    preOpImages: Blob[],
    postOpImages: Blob[],
  ): Promise<TImageResolutionItem[]> => {
    const beforeRows = await Promise.all(
      preOpImages.map(async (blob, idx) => {
        const { width, height } = await getBlobDimensions(blob);
        return {
          key: `before-${idx}`,
          group: "Before" as const,
          sourceType: "preOp" as const,
          sourceIndex: idx,
          width,
          height,
          minDimension: Math.min(width, height),
          blob,
        };
      }),
    );

    const afterRows = await Promise.all(
      postOpImages.map(async (blob, idx) => {
        const { width, height } = await getBlobDimensions(blob);
        return {
          key: `after-${idx}`,
          group: "After" as const,
          sourceType: "postOp" as const,
          sourceIndex: idx,
          width,
          height,
          minDimension: Math.min(width, height),
          blob,
        };
      }),
    );

    return [...beforeRows, ...afterRows]
      .map(({ blob, ...row }) => ({
        ...row,
        previewUrl: URL.createObjectURL(blob),
      }))
      .sort((a, b) => a.minDimension - b.minDimension);
  };

  const evaluateSmallImageWarning = async (
    preOpImages: Blob[],
    postOpImages: Blob[],
  ) => {
    const resolutionRows = await buildResolutionRows(preOpImages, postOpImages);
    if (!isMountedRef.current) {
      resolutionRows.forEach((row) => URL.revokeObjectURL(row.previewUrl));
      return false;
    }

    const hasSmallImages = resolutionRows.some((row) => row.minDimension < 400);

    if (hasSmallImages) {
      replaceSmallImageRows(resolutionRows);
      setShowSmallImageModal(true);
      return true;
    }

    clearSmallImageRows();
    return false;
  };

  const proceedToGenerate = () => {
    setStep(2);
    handleGenerateBeforeAfter();
    markStepComplete(TOOLTIP_STEPS.GENERATE_IMAGES);
  };

  const handleGeneratePress = async () => {
    if (isCheckingImageDimensionsRef.current) return;
    isCheckingImageDimensionsRef.current = true;
    setIsCheckingImageDimensions(true);

    const activeElement = document.activeElement as HTMLElement | null;
    smallImageTriggerRef.current =
      activeElement?.getAttribute("aria-label") === "Generate"
        ? activeElement
        : document.querySelector<HTMLElement>('[aria-label="Generate"]');
    const totalImages =
      uploadedImages.preOp.length + uploadedImages.postOp.length;

    try {
      if (totalImages === 0) {
        smallImageTriggerRef.current = null;
        proceedToGenerate();
        return;
      }

      const shouldWarn = await evaluateSmallImageWarning(
        uploadedImages.preOp,
        uploadedImages.postOp,
      );
      if (!isMountedRef.current) return;
      if (shouldWarn) {
        return;
      }
    } catch (error) {
      console.error("Error checking image dimensions:", error);
    } finally {
      isCheckingImageDimensionsRef.current = false;
      if (isMountedRef.current) setIsCheckingImageDimensions(false);
    }

    if (!isMountedRef.current) return;
    smallImageTriggerRef.current = null;
    proceedToGenerate();
  };

  const handleDeleteSmallImage = async (row: TImageResolutionItem) => {
    const nextPreOpImages =
      row.sourceType === "preOp"
        ? uploadedImages.preOp.filter((_, idx) => idx !== row.sourceIndex)
        : uploadedImages.preOp;
    const nextPostOpImages =
      row.sourceType === "postOp"
        ? uploadedImages.postOp.filter((_, idx) => idx !== row.sourceIndex)
        : uploadedImages.postOp;

    handleUpdateUploadedImages(nextPreOpImages, "preOp");
    handleUpdateUploadedImages(nextPostOpImages, "postOp");

    try {
      const shouldWarn = await evaluateSmallImageWarning(
        nextPreOpImages,
        nextPostOpImages,
      );
      if (!shouldWarn) {
        closeSmallImageModal();
      }
    } catch (error) {
      console.error("Error refreshing small image warning list:", error);
      closeSmallImageModal();
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <RainbowBorder
        isActive={
          tipsEnabled &&
          tipsState.usePlaceholderImages &&
          !tipsState.useAISettings
        }
        borderRadius={12}
      >
        <View style={styles.placeholderToggleContainer}>
          <NormalText
            text={"Use Image Placeholders:"}
            style={{
              fontSize: tokens.fontSize.md,
              marginRight: tokens.spacing.md,
            }}
          />
          <Switch
            accessibilityLabel="Use image placeholders"
            value={usePlaceholderImages}
            onValueChange={(val) => {
              setUsePlaceholderImages(val);
              if (
                val &&
                tipsState.usePlaceholderImages &&
                !tipsState.useAISettings
              ) {
                markStepComplete("usePlaceholderImages");
              }
            }}
            disabled={
              loading || isPreparingUploads || isCheckingImageDimensions
            }
          />
        </View>
      </RainbowBorder>

      {placeholderRequestStatus === "loading" ? (
        <View
          style={styles.placeholderStatus}
          accessibilityRole="progressbar"
          accessibilityLiveRegion="polite"
        >
          <ActivityIndicator size="small" color={tokens.color.accentDark} />
          <Text style={styles.placeholderStatusText}>
            Loading placeholder images…
          </Text>
        </View>
      ) : null}

      {placeholderRequestStatus === "error" ? (
        <View style={styles.placeholderError}>
          <Text
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
            style={styles.placeholderErrorText}
          >
            {placeholderRequestError}
          </Text>
          <SecondaryButton
            text="Retry"
            accessibilityLabel="Retry placeholder images"
            onPress={() => setPlaceholderRetryKey((key) => key + 1)}
          />
        </View>
      ) : null}

      <View
        testID="before-after-upload-panels"
        style={[
          styles.imagesUploadContainer,
          usesStackedUploads && styles.imagesUploadContainerStacked,
        ]}
      >
        <MultiImageUpload
          label="Pre-Op Images"
          images={uploadedImages.preOp}
          onImagesChange={(images) =>
            handleUpdateUploadedImages(images, "preOp")
          }
          disabled={usePlaceholderImages || isCheckingImageDimensions}
          loading={loading || placeholderRequestStatus === "loading"}
          inputAccessibilityLabel="Choose pre-op photos"
          testIDPrefix="before-after-preop"
          previewsInsideDropZone
          onPreparingChange={setIsPreparingPreOpImages}
          style={
            usesStackedUploads
              ? styles.uploadPanelStacked
              : styles.uploadPanelDesktop
          }
        />
        <MultiImageUpload
          label="Post-Op Images"
          images={uploadedImages.postOp}
          onImagesChange={(images) =>
            handleUpdateUploadedImages(images, "postOp")
          }
          disabled={usePlaceholderImages || isCheckingImageDimensions}
          loading={loading || placeholderRequestStatus === "loading"}
          inputAccessibilityLabel="Choose post-op photos"
          testIDPrefix="before-after-postop"
          previewsInsideDropZone
          onPreparingChange={setIsPreparingPostOpImages}
          style={
            usesStackedUploads
              ? styles.uploadPanelStacked
              : styles.uploadPanelDesktop
          }
        />
      </View>

      <RainbowBorder
        isActive={
          tipsEnabled && tipsState.useAISettings && !tipsState.generateImages
        }
        borderRadius={12}
      >
        <AISetting
          disabled={loading}
          selectedAiSetting={aiSetting}
          setAiSetting={onAiSettingChange}
        />
      </RainbowBorder>

      <View style={styles.buttonContainer}>
        <RainbowBorder
          isActive={
            tipsEnabled && tipsState.generateImages && !tipsState.editTemplate
          }
          borderRadius={12}
        >
          <PrimaryButton
            text="Generate"
            icon={<Picture />}
            width={200}
            disabled={
              isPreparingUploads ||
              (!canGenerateWithoutUploads &&
                (uploadedImages.preOp.length === 0 ||
                  uploadedImages.postOp.length === 0))
            }
            loading={loading || isCheckingImageDimensions}
            onPress={() => void handleGeneratePress()}
            roundBorderRadius
          />
        </RainbowBorder>
        <SecondaryButton
          text="Reset Images"
          icon={<Restart />}
          onPress={() => {
            setUsePlaceholderImages(false);
            handleUpdateUploadedImages([], "preOp");
            handleUpdateUploadedImages([], "postOp");
          }}
          disabled={isPreparingUploads || isCheckingImageDimensions}
          width={200}
          roundBorderRadius
        />
      </View>
      {tipsEnabled && <StickyOnboardingModal steps={beforeAfterSteps} />}

      <Modal
        visible={showSmallImageModal}
        transparent
        animationType="fade"
        onRequestClose={closeSmallImageModal}
        onDismiss={restoreSmallImageTriggerFocus}
        accessibilityViewIsModal
      >
        <View style={styles.modalOverlay}>
          <div
            ref={smallImageDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="small-image-warning-title"
            aria-describedby="small-image-warning-description"
            onKeyDown={handleSmallImageDialogKeyDown}
            style={StyleSheet.flatten(styles.modalCard) as React.CSSProperties}
          >
            <Text
              nativeID="small-image-warning-title"
              accessibilityRole="header"
              style={styles.modalTitle}
            >
              Small Image Warning
            </Text>
            <Text
              nativeID="small-image-warning-description"
              style={styles.modalBody}
            >
              Some uploaded images are small and may look blurry in generated
              before/after outputs.
            </Text>

            <View style={styles.legendRow}>
              <View style={[styles.legendChip, styles.legendYellow]} />
              <Text style={styles.legendText}>{`< 400px`}</Text>
              <View style={[styles.legendChip, styles.legendRed]} />
              <Text style={styles.legendText}>{`< 200px`}</Text>
            </View>

            <ScrollView style={styles.resolutionList}>
              {smallImageRows.map((row) => {
                const isVerySmall = row.minDimension < 200;
                const isSmall = row.minDimension < 400;
                return (
                  <View
                    key={row.key}
                    style={[
                      styles.resolutionRow,
                      isVerySmall
                        ? styles.rowCritical
                        : isSmall
                          ? styles.rowWarning
                          : styles.rowNormal,
                    ]}
                  >
                    <View style={styles.resolutionRowLeft}>
                      <View style={styles.imagePreviewContainer}>
                        <img
                          src={row.previewUrl}
                          alt={`${row.group} ${row.sourceIndex + 1}`}
                          style={styles.imagePreview}
                        />
                      </View>
                      <Text style={styles.resolutionRowText}>
                        {`${row.group} ${row.sourceIndex + 1}: ${row.width} x ${row.height}`}
                      </Text>
                    </View>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Delete ${row.group.toLowerCase()} image ${row.sourceIndex + 1}`}
                      onFocus={() => setFocusedDeleteKey(row.key)}
                      onBlur={() => setFocusedDeleteKey(null)}
                      style={[
                        styles.deleteButton,
                        focusedDeleteKey === row.key &&
                          styles.warningActionFocused,
                      ]}
                      onPress={() => void handleDeleteSmallImage(row)}
                    >
                      <Text style={styles.deleteButtonText}>Delete</Text>
                    </Pressable>
                  </View>
                );
              })}
            </ScrollView>

            <View style={styles.modalActions}>
              <SecondaryButton
                text="Cancel"
                accessibilityLabel="Cancel small image warning"
                onPress={closeSmallImageModal}
              />
              <PrimaryButton
                text="Continue Anyway"
                accessibilityLabel="Continue with small images"
                onPress={() => {
                  closeSmallImageModal();
                  proceedToGenerate();
                }}
              />
            </View>
          </div>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    width: "100%",
    maxWidth: STEP_ONE_TOKENS.size.contentMaxWidth,
    alignSelf: "center",
    paddingHorizontal: tokens.spacing.lg,
    paddingBottom: tokens.spacing.lg,
    gap: tokens.spacing.lg,
  },
  buttonContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: tokens.spacing.sm,
    justifyContent: "center",
  },
  imagesUploadContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
    minWidth: 0,
    gap: tokens.spacing.lg,
  },
  imagesUploadContainerStacked: {
    flexDirection: "column",
  },
  uploadPanelDesktop: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  },
  uploadPanelStacked: {
    width: "100%",
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
  },
  placeholderToggleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: tokens.spacing.md,
    marginBottom: tokens.spacing.xs,
  },
  placeholderStatus: {
    minHeight: tokens.spacing.massive,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacing.sm,
    padding: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.border,
    backgroundColor: tokens.color.surfaceMuted,
  },
  placeholderStatusText: {
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  placeholderError: {
    alignItems: "center",
    gap: tokens.spacing.md,
    padding: tokens.spacing.lg,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.errorBorder,
    backgroundColor: tokens.color.errorSurface,
  },
  placeholderErrorText: {
    color: tokens.color.errorText,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    textAlign: "center",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: tokens.color.overlay,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: tokens.spacing.lg,
  },
  modalCard: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: STEP_ONE_TOKENS.size.warningModalMaxWidth,
    backgroundColor: tokens.color.surface,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.lg,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.border,
  },
  modalTitle: {
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.lg,
    lineHeight: tokens.lineHeight.lg,
    color: tokens.color.textPrimary,
    marginBottom: tokens.spacing.sm,
  },
  modalBody: {
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    color: tokens.color.textSecondary,
    marginBottom: tokens.spacing.md,
  },
  legendRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.sm,
    marginBottom: tokens.spacing.md,
  },
  legendChip: {
    width: tokens.spacing.lg,
    height: tokens.spacing.lg,
    borderRadius: tokens.radius.sm,
  },
  legendYellow: {
    backgroundColor: tokens.color.surfaceAccent,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.accent,
  },
  legendRed: {
    backgroundColor: tokens.color.errorSurface,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.errorBorder,
    marginLeft: tokens.spacing.sm,
  },
  legendText: {
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.xs,
    lineHeight: tokens.lineHeight.xs,
    color: tokens.color.textSecondary,
    marginRight: tokens.spacing.sm,
  },
  resolutionList: {
    maxHeight: STEP_ONE_TOKENS.size.warningListMaxHeight,
    marginBottom: tokens.spacing.md,
  },
  resolutionRow: {
    borderRadius: tokens.radius.md,
    borderWidth: tokens.borderWidth.thin,
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    marginBottom: tokens.spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacing.md,
  },
  resolutionRowLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.md,
    flex: 1,
    minWidth: 0,
  },
  imagePreviewContainer: {
    width: STEP_ONE_TOKENS.size.warningPreview,
    height: STEP_ONE_TOKENS.size.warningPreview,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.border,
    backgroundColor: tokens.color.surface,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  imagePreview: {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
  },
  rowNormal: {
    backgroundColor: tokens.color.surfaceMuted,
    borderColor: tokens.color.border,
  },
  rowWarning: {
    backgroundColor: tokens.color.surfaceAccent,
    borderColor: tokens.color.accent,
  },
  rowCritical: {
    backgroundColor: tokens.color.errorSurface,
    borderColor: tokens.color.errorBorder,
  },
  resolutionRowText: {
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    color: tokens.color.textPrimary,
    flexShrink: 1,
  },
  deleteButton: {
    minHeight: tokens.spacing.huge + tokens.spacing.xs,
    minWidth: tokens.spacing.giant,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.borderWidth.thin,
    borderColor: tokens.color.errorText,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    backgroundColor: tokens.color.surface,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  deleteButtonText: {
    color: tokens.color.errorText,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.xs,
    lineHeight: tokens.lineHeight.xs,
  },
  warningActionFocused: {
    outlineColor: tokens.color.accentDark,
    outlineOffset: tokens.spacing.xs,
    outlineStyle: "solid",
    outlineWidth: tokens.borderWidth.strong,
  } as any,
  modalActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: tokens.spacing.sm,
  },
});

export default Step1;
